import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import {
  normalizeProductSku,
  productSchema,
  ProductStatus,
} from '@parallel-fluidics/shared/models/products';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import createProduct from '../apis/createProduct';
import updateProduct from '../apis/updateProduct';

const statusOptions = [
  { label: 'Draft', value: ProductStatus.DRAFT },
  { label: 'Active', value: ProductStatus.ACTIVE },
  { label: 'Retired', value: ProductStatus.RETIRED },
];

export default function ProductFormModal({
  open,
  onClose,
  onSuccess = () => {},
  isEditing = false,
  initProduct = null,
}) {
  const [submitError, setSubmitError] = useState('');

  const initialValues = useMemo(() => ({
    sku: initProduct?.sku || '',
    name: initProduct?.name || '',
    description: initProduct?.description || '',
    status: initProduct?.status || ProductStatus.DRAFT,
    labelQrHeader: initProduct?.labelQrHeader || '',
    labelQrUrl: initProduct?.labelQrUrl || '',
  }), [initProduct]);

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validate: (validateValues) => {
      const validationErrors = {};

      try {
        productSchema.validateSync({
          sku: validateValues.sku,
          name: validateValues.name,
          description: validateValues.description,
          status: validateValues.status,
          activeVersionId: initProduct?.activeVersionId || null,
          labelQrHeader: validateValues.labelQrHeader?.trim() || null,
          labelQrUrl: validateValues.labelQrUrl?.trim() || null,
        }, { abortEarly: false });
      } catch (error) {
        error.inner?.forEach((validationError) => {
          if (!validationErrors[validationError.path]) {
            validationErrors[validationError.path] = validationError.message;
          }
        });
      }

      if (isEditing
        && validateValues.status === ProductStatus.ACTIVE
        && !initProduct?.activeVersionId) {
        validationErrors.status = 'Activate a version first before setting the product to Active';
      }

      return validationErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        setSubmitError('');
        const savedProductDocId = isEditing
          ? await updateProduct(initProduct.id, {
            sku: submitValues.sku,
            name: submitValues.name,
            description: submitValues.description,
            status: submitValues.status,
            labelQrHeader: submitValues.labelQrHeader,
            labelQrUrl: submitValues.labelQrUrl,
          })
          : await createProduct({
            sku: submitValues.sku,
            name: submitValues.name,
            description: submitValues.description,
            labelQrHeader: submitValues.labelQrHeader,
            labelQrUrl: submitValues.labelQrUrl,
          });
        onSuccess(savedProductDocId);
        onClose();
      } catch (error) {
        setSubmitError(error.message || 'Unable to save product');
      }
    },
  });

  return (
    <Modal
      onDismiss={onClose}
      visible={open}
      header={isEditing ? 'Edit product' : 'Create product'}
    >
      <form onSubmit={handleSubmit}>
        <SpaceBetween direction="vertical" size="s">
          {submitError ? (
            <Alert type="error">{submitError}</Alert>
          ) : null}

          <FormField
            label="SKU"
            description={isEditing && initProduct?.activeVersionId
              ? 'SKU cannot be changed after a version has been activated.'
              : 'Uppercase letters, numbers, periods, underscores, or hyphens.'}
            errorText={errors.sku}
          >
            <Input
              value={values.sku}
              placeholder="LNP-001"
              disabled={isEditing && !!initProduct?.activeVersionId}
              onChange={({ detail }) => setFieldValue('sku', normalizeProductSku(detail.value))}
            />
          </FormField>

          <FormField label="Product name" errorText={errors.name}>
            <Input
              value={values.name}
              onChange={({ detail }) => setFieldValue('name', detail.value)}
            />
          </FormField>

          <FormField label="Description" errorText={errors.description}>
            <Textarea
              value={values.description}
              onChange={({ detail }) => setFieldValue('description', detail.value)}
            />
          </FormField>

          <FormField
            label="Label QR header"
            description="Short text above the QR code on the label, e.g. “Quick-Start”."
            errorText={errors.labelQrHeader}
          >
            <Input
              value={values.labelQrHeader}
              onChange={({ detail }) => setFieldValue('labelQrHeader', detail.value)}
            />
          </FormField>

          <FormField
            label="Label QR link"
            description="Customer-facing link printed as the QR code on product labels, e.g. a quick-start guide."
            errorText={errors.labelQrUrl}
          >
            <Input
              value={values.labelQrUrl}
              placeholder="https://…"
              onChange={({ detail }) => setFieldValue('labelQrUrl', detail.value)}
            />
          </FormField>

          {isEditing && (
            <FormField
              key="status"
              label="Status"
              errorText={errors.status}
              description={!initProduct?.activeVersionId ? 'Activate a version before setting this to Active.' : undefined}
            >
              <Select
                selectedOption={statusOptions.find((o) => o.value === values.status) || null}
                options={statusOptions}
                onChange={({ detail }) => setFieldValue('status', detail.selectedOption.value)}
              />
            </FormField>
          )}

          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" formAction="none" onClick={onClose}>Cancel</Button>
              <Button variant="primary" type="submit" loading={isSubmitting}>
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </form>
    </Modal>
  );
}

ProductFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  isEditing: PropTypes.bool,
  initProduct: PropTypes.shape({
    id: PropTypes.string,
    sku: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    activeVersionId: PropTypes.string,
    labelQrHeader: PropTypes.string,
    labelQrUrl: PropTypes.string,
  }),
};
